import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';

import { environment } from 'environments/environment';
import { InvestProject } from '@classes/invest/invest-project';
import { InvestItemDetail } from '@classes/invest/invest-simulation/invest-item-detail';
import { InvestItem } from '@classes/invest/invest-simulation/invest-item';
import { Invest } from '@classes/invest/invest';
import { InvestSic } from '@classes/invest/invest-sic/invest-sic';
import { InvestBbc } from '@classes/invest/invest-bbc/invest-bbc';
import { ProviderType } from '@enums/provider-type.enum';
import { UiService } from './ui.service';
import { User } from '@classes/licence/user';

@Injectable({
    providedIn: 'root'
})
export class InvestService {
    investChanged = new Subject<Invest>();

    private baseUrl = environment.apiUrl + 'investments';

    constructor(
        private http: HttpClient,
        private uiSrv: UiService
    ) { }

    getListByUser(): Observable<User[]> {
        return this.http.get<User[]>(this.baseUrl);
    }

    get(simulationCode: string): Observable<Invest> {
        this.uiSrv.routeLoadingStateChanged.next(true);

        return this.http.get<Invest>(`${this.baseUrl}/${simulationCode}`)
        .pipe(
            tap(invest => {
                this.uiSrv.routeLoadingStateChanged.next(false);
                this.investChanged.next(invest);
            })
        );
    }

    getProjects(investCode: string, provider: ProviderType): Observable<InvestProject[]> {
        this.uiSrv.routeLoadingStateChanged.next(true);

        let url = `${this.baseUrl}/${investCode}/`;

        if(provider === ProviderType.bbc) {
            url += 'bbc';
        } else {
            url += 'sic';
        }

        return this.http.get<InvestProject[]>(`${url}/projects`)
        .pipe(
            tap(() => {
                this.uiSrv.routeLoadingStateChanged.next(false);
            })
        );
    }

    calculate(invest: Invest): Observable<Invest> {
        let url = this.baseUrl;

        if(invest.simulation.provider === ProviderType.bbc) {
            url += '/bbc';
        } else {
            url += '/sic';
        }

        return this.http.post<Invest>(`${url}/calculate`, invest)
        .pipe(
            tap(result => {
                this.investChanged.next(result);
            })
        );
    }

    post(invest: Invest): Observable<Invest> {
        let url = this.baseUrl;

        if(invest.simulation.provider === ProviderType.bbc) {
            url += '/bbc';
        } else {
            url += '/sic';
        }

        return this.http.post<Invest>(`${url}`, invest);
    }

    put(invest: Invest): Observable<Invest> {
        let url = this.baseUrl;

        if(invest.simulation.provider === ProviderType.bbc) {
            url += '/bbc';
        } else {
            url += '/sic';
        }

        return this.http.put<Invest>(`${url}`, invest);
    }

    calculateExtraFund(invest: InvestSic): void {
        let lastValue: number;

        invest.simulation.years.filter(y => y.isEstimated == null || y.isEstimated)
        .map(simuYear => {
            const extra = simuYear.items.find(i => i.code === 'extraFund');
            const variation = simuYear.items.find(i => i.code === 'fundVariation');

            if(lastValue == null) {
                lastValue = extra.value;
            } else {
                lastValue += variation.value;
                extra.value = lastValue;
            }

            lastValue -= this.sumProjectsYearProperty(invest.fundingProject.projects, simuYear.year, 'extraFund');
        });
    }

    // Sum value of a selected property for a selected year in multiple projects
    sumProjectsYearProperty(projects: InvestProject[], selYear: number, selKey: string): number {
        return projects.reduce((sum, project) => {
            return sum + project.years.filter(y => y.year === selYear)
            .reduce((total, projectYear) => {
                return total + projectYear[selKey];
            }, 0);
        }, 0);
    }

    calculateBbcTots(invest: InvestBbc): InvestBbc {
        const childrenItems: InvestItemDetail[] = invest.simulation.itemsDetail.filter(i => i.parentCode != null);

        invest.simulation.years.filter(y => y.isEstimated == null || y.isEstimated)
        .map(simuYear => {
            // First calculate children
            simuYear.items.filter(i => childrenItems.some(ci => ci.code === i.code))
            .map(yearItem => {
                this.calculateChildItem(simuYear.year, yearItem, invest.projects);
            });

            // Then sum all items
            simuYear.items.map(yearItem => {
                this.calculateParentItem(yearItem, simuYear.items);
            });
        });

        return invest;
    }

    private calculateChildItem(year: number, item: InvestItem, projects: InvestProject[]) {
        switch(item.code) {
            case 'ontvangsten.investering':
                const subsidiesSum: number = this.sumProjectsYearProperty(projects, year, 'subsidies');
                const saleSum: number = this.sumProjectsYearProperty(projects, year, 'sale');

                item.value = item.baseValue + subsidiesSum + saleSum;
                break;
            case 'ontvangsten.financiering':
                const loaningSum: number = this.sumProjectsYearProperty(projects, year, 'loaningAmount');
                item.value = item.baseValue + loaningSum;
                break;
        }
    }

    private calculateParentItem(yearItem: InvestItem, yearItems: InvestItem[]) {
        let codes: string[];
        let sign: string;

        switch(yearItem.code) {
            case 'bbc2020.exploitatieSaldo':
                codes = ['ontvangsten.exploitatie', 'uitgaven.exploitatie'];
                sign = '-';
                break;
            case 'bbc2020.investeringsSaldo':
                codes = ['ontvangsten.investering', 'uitgaven.investering'];
                sign = '-';
                break;
            case 'bbc2020.saldoExploitatieEnInvesteringen':
                codes = ['bbc2020.exploitatieSaldo', 'bbc2020.investeringsSaldo'];
                sign = '+';
                break;
            case 'bbc2020.financieringsSaldo':
                codes = ['ontvangsten.financiering', 'uitgaven.financiering'];
                sign = '-';
                break;
            case 'bbc2020.budgettairResultaat':
                codes = ['bbc2020.saldoExploitatieEnInvesteringen', 'bbc2020.financieringsSaldo'];
                sign = '+';
                break;
            case 'bbc2020.cumul':
                codes = ['bbc2020.budgettairResultaat', 'bbc2020.gecumuleerdBudgettairResultaat'];
                sign = '+';
                break;
            case 'bbc2020.beschikbaarBudgettairResultaat':
                codes = ['bbc2020.cumul', 'bbc2020.onbeschikbareGelden'];
                sign = '-';
                break;
            default:
                codes = [];
                sign = null;
        }

        if(codes.length && sign != null) {
            yearItem.value = this.sumItemsValues(yearItems, codes, sign);
        }
    }

    private sumItemsValues(yearItems: InvestItem[], selCodes: string[], sign: string): number {
        return yearItems.filter(item => (selCodes.indexOf(item.code) !== -1))
        .reduce((tot, item) => {
            if(sign === '+' || tot === 0) {
                return tot + item.value;
            }

            return tot - item.value;
        } , 0);
    }
}
